import { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useRecoilValue, useResetRecoilState, useSetRecoilState } from 'recoil'

import { CMD_TYPE, TELECOM } from '@/shared/constants/common'
import useAPI from '@/shared/hooks/useAPI.hook'
import useCookies from '@/shared/hooks/useCookies.hook'
import useLoading from '@/shared/hooks/useLoading.hook'
import useModal from '@/shared/hooks/useModal.hook'
import { RefreshRecapAuthToken } from '@/shared/recoil/common/atom'
import {
  isAgreeAllRequiredTermsData,
  otpAuthType,
  otpjoinready,
  otpTimeOut,
  otpTransctionID,
} from '@/shared/recoil/otp/atom'
import {
  getReqAddUserOTP,
  getReqCertDelete,
  getReqCertJoinPartner,
  getReqCertJoinReady,
  getReqCertLogin,
  getReqSendOTP,
  otpWithSendReset,
} from '@/shared/recoil/otp/withSendAndCert'
import { qrLoginCheck, resLoginUser } from '@/shared/recoil/user/atom'
import {
  btnDisabled,
  inputDisabled,
  ServerErrMsg,
  validateTypeFam,
} from '@/shared/recoil/validate/atom'

export const useSendOTP = () => {
  const reqSendOTP = useRecoilValue(getReqSendOTP)
  const reqAddUserSendOTP = useRecoilValue(getReqAddUserOTP)
  const setOtpTimeOut = useSetRecoilState(otpTimeOut)
  const setOtpTransactionID = useSetRecoilState(otpTransctionID)
  const authType = useRecoilValue(otpAuthType)
  const loading = useLoading()
  const api = useAPI()
  const certBtnDisabled = useSetRecoilState(btnDisabled('certBtn'))
  const telDisabled = useSetRecoilState(inputDisabled('telecom'))
  const setRefreshReCaptcha = useSetRecoilState(RefreshRecapAuthToken)
  async function sendOTP() {
    loading.on()
    authType === 'login' && setRefreshReCaptcha((r: boolean) => !r)
    const result = await api.requestSRS<ResponseSendOTPBody>(
      CMD_TYPE.SEND_OTP,
      authType === 'fadd' ? reqAddUserSendOTP : reqSendOTP,
    )
    if (result) {
      if (typeof result === 'string') {
        if (result === '030005') {
          telDisabled({ isDisabled: true })
          certBtnDisabled({ isDisabled: false })
          loading.off()
          return true
        }
      }
      setOtpTimeOut(result.TimeOut)
      setOtpTransactionID(result.TransactionID)
      telDisabled({ isDisabled: true })
      certBtnDisabled({ isDisabled: false })
      loading.off()
      return true
    } else {
      loading.off()
      return false
    }
  }

  return {
    request: sendOTP,
  }
}

export const useCertOTP = () => {
  const loading = useLoading()
  const isAgreeAllRequiredTerms = useRecoilValue(isAgreeAllRequiredTermsData)
  const reqCertJoinPartner = useRecoilValue(getReqCertJoinPartner)
  const reqCertJoinReady = useRecoilValue(getReqCertJoinReady)
  const reqCertLogin = useRecoilValue(getReqCertLogin)
  const reqCertDel = useRecoilValue(getReqCertDelete)
  const setLogin = useSetRecoilState(resLoginUser)
  const resetSendOTP = useSetRecoilState(otpWithSendReset)
  const setTermsValid = useSetRecoilState(validateTypeFam('terms'))
  const setJoinCheck = useSetRecoilState(otpjoinready)
  const resetQRLogin = useResetRecoilState(qrLoginCheck)
  const qrLoginChecker = useRecoilValue(qrLoginCheck)
  const cookie = useCookies()
  const api = useAPI()
  const modal = useModal()
  const navigate = useNavigate()
  const [vulnerableModalVisible, setVulnerableModalVisible] = useState(false)
  const vulnerableModalPromiseRef = useRef<{
    resolve: (value: boolean) => void
    reject: (reason?: any) => void
  } | null>(null)

  function join_complete_message(isMoveMyPage: boolean) {
    if (isMoveMyPage) {
      modal.confirm({
        title: '서비스 가입이 완료되었습니다',
        contents: `마이페이지에서 추가연락처,
          추가 차량을 등록하실 수 있습니다.`,
        onCancel: () => {
          resetSendOTP((cur) => cur)
          navigate('/')
        },
        onConfirm: () => {
          resetSendOTP((cur) => cur)
          navigate('/mypage')
        },
      })
    } else {
      modal.alert({
        title: '서비스 가입이 완료되었습니다',
        contents: '오토콜의 다양한 서비스를 이용해 보세요.',
        onCancel: () => {
          resetSendOTP((cur) => cur)
          navigate('/')
        },
        onConfirm: () => {
          resetSendOTP((cur) => cur)
          navigate('/success')
        },
      })
    }
  }

  async function join_ready() {
    const result = await api.requestSRS<boolean>(CMD_TYPE.CERT_OTP, reqCertJoinReady)
    if (result) {
      loading.off()
      setJoinCheck(true)
      return true
    } else {
      loading.off()
      setJoinCheck(false)
      return false
    }
  }

  async function join_partner() {
    loading.on()

    if (reqCertJoinPartner.Telecom === TELECOM.KT) {
      if (!isAgreeAllRequiredTerms) {
        setTermsValid({ valid: true, msg: '210' })
        loading.off()
        return false
      } else {
        setTermsValid({ valid: false, msg: '' })
      }
      if (!isAgreeAllRequiredTerms) {
        loading.off()
        setTermsValid({ valid: true, msg: '210' })
        return false
      } else {
        setTermsValid({ valid: false, msg: '' })
      }
    }

    const isVulnerable = await api.requestSRS<ResponseCheckVulnerableBody>(
      CMD_TYPE.CHECK_VULNERABLE,
      {
        Telecom: reqCertJoinPartner.Telecom,
        PNumber: reqCertJoinPartner.PNumber,
      },
    )

    let isKeepGoing = true
    if (!!isVulnerable) {
      const { Vulnerable, Message, AgePopUpYN } = isVulnerable

      if (Vulnerable === 'Y' || AgePopUpYN === 'Y') {
        await new Promise<boolean>((resolve, reject) => {
          vulnerableModalPromiseRef.current = { resolve, reject }
          setVulnerableModalVisible(true)
        }).catch(() => {
          isKeepGoing = false
        })
      } else if (Vulnerable === 'X') {
        await modal
          .alert({
            title: '서비스 안내',
            contents: Message,
            onConfirm: () => {
              isKeepGoing = false
            },
          })
          .catch(() => {
            isKeepGoing = false
          })
      }
    } else {
      await modal
        .alert({
          title: '서비스 안내',
          contents: ServerErrMsg['030039'],
          onConfirm: () => {
            isKeepGoing = false
          },
        })
        .catch(() => {
          isKeepGoing = false
        })
    }
    if (!isKeepGoing) {
      loading.off()
      return false
    }

    const result = await api.requestSRS<ResponseLoginBody>(CMD_TYPE.CERT_OTP, reqCertJoinPartner)
    if (result?.data?.Header?.ErrCode === '030004') {
      loading.off()
      window.location.reload()
      return { success: false, errCode: '030004', data: result }
    }
    if (result != null) {
      setLogin(result)
      cookie.login(result.PNumber, result.SessionID, String(result.Telecom))
      loading.off()

      modal.alert({
        title: '오토콜 안내',
        contents:
          '휴대폰 번호 없이 차량에 대한 이동 요청 알림을 받을 수 있는 안심 유료부가서비스에 가입되셨습니다. 차량 이동 알림의 원활한 수신을 위해 [확인] 버튼을 눌러 차량 번호를 입력하세요.',
        onCancel: () => {},
        onConfirm: () => {
          join_complete_message(result.IsFreeMember || (result.AlreadyRecvSticker ?? false))
        },
      })
      return true
    } else {
      loading.off()
      return false
    }
  }

  async function login() {
    loading.on()
    const result = await api.requestSRS<ResponseLoginBody>(CMD_TYPE.CERT_OTP, reqCertLogin)
    if (result && String(result)?.length) {
      setLogin(result)
      cookie.login(result.PNumber, result.SessionID, String(result.Telecom))
      resetSendOTP((cur) => cur)
      loading.off()
      setTimeout(() => {
        if (qrLoginChecker) {
          resetQRLogin()
          navigate('/qr')
        } else {
          window.location.reload()
        }
      }, 50)
      return true
    } else {
      loading.off()
    }
  }

  async function cancel() {
    loading.on()
    const result = await api.requestSRS<unknown>(CMD_TYPE.CERT_OTP, reqCertDel)
    if (result) {
      cookie.logout()
      resetSendOTP((cur) => cur)
      loading.off()
      return true
    } else {
      loading.off()
      return false
    }
  }

  const handleVulnerableModalCancel = () => {
    setVulnerableModalVisible(false)
    if (vulnerableModalPromiseRef.current) {
      vulnerableModalPromiseRef.current.reject()
      vulnerableModalPromiseRef.current = null
    }
  }

  const handleVulnerableModalConfirm = () => {
    setVulnerableModalVisible(false)
    if (vulnerableModalPromiseRef.current) {
      vulnerableModalPromiseRef.current.resolve(true)
      vulnerableModalPromiseRef.current = null
    }
  }

  return {
    join_partner,
    join_ready,
    login,
    cancel,
    vulnerableModalProps: {
      visible: vulnerableModalVisible,
      onCancel: handleVulnerableModalCancel,
      onConfirm: handleVulnerableModalConfirm,
      telecom: reqCertJoinPartner.Telecom,
    },
  }
}
